<template>
  <div class="modal-card" style="width: 620px; max-width: 100%">
    <header class="modal-card-head">
      <p class="modal-card-title">
        <b-icon pack="fas" icon="sms" size="is-small"></b-icon>
        {{ t("Enviar SMS", "Send SMS") }}
      </p>
      <button type="button" class="delete" @click="$emit('close')"></button>
    </header>

    <section class="modal-card-body">
      <p class="mb-3">
        <b>{{ t("Para", "To") }}:</b> {{ nombre }}
        <b-tag type="is-light" class="ml-1">{{ rolLabel }}</b-tag>
      </p>

      <!-- El número se arma con lo guardado en el perfil, pero muchos socios lo tienen
           sin código de país: el agente lo revisa antes de enviar -->
      <b-field
        :label="t('Número (con código de país)', 'Number (with country code)')"
        :type="telefonoValido ? '' : 'is-danger'"
        :message="telefonoValido ? numeroOrigen : t('Debe empezar con + y el código de país, de 8 a 15 dígitos', 'Must start with + and the country code, 8 to 15 digits')"
      >
        <b-input v-model="telefono" placeholder="+13055550123" icon-pack="fas" icon="phone"></b-input>
      </b-field>
      <b-message v-if="telefonoValido && !esUsa" type="is-warning" size="is-small">
        {{ t("Este número no es de EE. UU./Canadá (+1): el SMS saldrá desde un número compartido y el socio no podrá responder.", "This number is not US/Canada (+1): the SMS will come from a shared number and the member won't be able to reply.") }}
      </b-message>

      <b-field :label="t('Mensaje', 'Message')" :type="noPermitidos.length || excedeMaximo ? 'is-danger' : ''">
        <b-input v-model="mensaje" type="textarea" rows="5" :has-counter="false"></b-input>
      </b-field>

      <!-- Cuántos SMS son: lo que se cobra -->
      <div class="sms-contador" :class="{ 'is-excedido': excedeMaximo, 'is-varios': sms > 1 && !excedeMaximo }">
        <b>{{ sms }} SMS</b>
        <span>&nbsp;· {{ septetos }}/{{ maxSeptetos }} {{ t("caracteres", "characters") }}</span>
        <span v-if="sms === 1"> · {{ t("quedan", "left") }} {{ 160 - septetos }} {{ t("para seguir en 1 SMS", "to stay in 1 SMS") }}</span>
        <span v-else-if="sms > 1 && !excedeMaximo"> · {{ t("se cobran", "charged as") }} {{ sms }} {{ t("mensajes", "messages") }}</span>
        <span v-if="excedeMaximo"> · {{ t("el máximo es", "the maximum is") }} {{ maxSms }} SMS</span>
      </div>

      <!-- Caracteres que no llegan bien por SMS: el agente decide si los corrige -->
      <b-message v-if="noPermitidos.length" type="is-danger" size="is-small" class="mt-3">
        <p>
          {{ t("Estos caracteres no llegan bien a todos los celulares y no se pueden enviar:", "These characters don't display on every phone and can't be sent:") }}
          <span v-for="c in noPermitidos" :key="c" class="sms-char">{{ visible(c) }}</span>
        </p>
        <p class="mt-2">
          {{ t("Al corregir quedaría así:", "After fixing it would read:") }}
          <i>“{{ textoCorregido }}”</i>
        </p>
        <b-button size="is-small" type="is-danger" icon-left="magic" icon-pack="fas" class="mt-2" @click="corregir">
          {{ t("Corregir automáticamente", "Fix automatically") }}
        </b-button>
      </b-message>

      <b-collapse :open="false" class="mt-3">
        <template #trigger="props">
          <a class="is-size-7">
            <b-icon pack="fas" :icon="props.open ? 'caret-down' : 'caret-right'" size="is-small"></b-icon>
            {{ t("¿Qué se permite en un SMS?", "What is allowed in an SMS?") }}
          </a>
        </template>
        <div class="content is-size-7 mt-2">
          <ul>
            <li>{{ t("Letras sin acento, números y signos comunes. También ñ, Ñ, é, É, ü, ¿ y ¡.", "Letters without accents, numbers and common punctuation. Also ñ, Ñ, é, É, ü, ¿ and ¡.") }}</li>
            <li>{{ t("No se permiten á, í, ó, ú (ni en mayúscula), comillas “ ”, emojis ni símbolos especiales: según el proveedor llegan cambiados o cuestan el doble.", "á, í, ó, ú (also uppercase), curly quotes, emojis and special symbols are not allowed: depending on the carrier they arrive altered or cost double.") }}</li>
            <li>{{ t("1 SMS = hasta 160 caracteres. Si se pasa, se divide en partes de 153 y cada parte se cobra. Máximo 3 SMS.", "1 SMS = up to 160 characters. Longer messages are split into 153-character parts and each part is charged. Maximum 3 SMS.") }}</li>
            <li>{{ t("€ [ ] { } ^ ~ | \\ cuentan como 2 caracteres.", "€ [ ] { } ^ ~ | \\ count as 2 characters.") }}</li>
          </ul>
        </div>
      </b-collapse>
    </section>

    <footer class="modal-card-foot" style="justify-content: flex-end">
      <b-button @click="$emit('close')">{{ t("Cancelar", "Cancel") }}</b-button>
      <b-button
        type="is-primary"
        icon-left="paper-plane"
        icon-pack="fas"
        :loading="enviando"
        :disabled="!puedeEnviar"
        @click="confirmar"
        >{{ t("Enviar", "Send") }} ({{ sms }} SMS)</b-button
      >
    </footer>
  </div>
</template>

<script>
import axios from "axios";
const API_URL = process.env.VUE_APP_API_URL;

// Alfabeto GSM-7: misma tabla que la API (utils/smsGsm.js). Con estos caracteres el SMS
// llega legible en cualquier celular sin depender del proveedor.
const GSM_BASICO =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXTENDIDO = "^{}\\[~]|€"; // cuentan 2
const SEPTETOS_UN_SMS = 160;
const SEPTETOS_POR_PARTE = 153;
const MAX_SMS = 3;
const REEMPLAZOS = {
  á: "a", â: "a", ã: "a", Á: "A", À: "A", Â: "A", Ã: "A",
  ê: "e", ë: "e", È: "E", Ê: "E", Ë: "E",
  í: "i", î: "i", ï: "i", Í: "I", Ì: "I", Î: "I", Ï: "I",
  ó: "o", ô: "o", õ: "o", Ó: "O", Ò: "O", Ô: "O", Õ: "O",
  ú: "u", û: "u", Ú: "U", Ù: "U", Û: "U",
  ç: "c", ý: "y", Ý: "Y",
  "‘": "'", "’": "'", "‚": "'", "`": "'", "´": "'",
  "“": '"', "”": '"', "„": '"', "«": '"', "»": '"',
  "–": "-", "—": "-", "‐": "-", "‑": "-", "•": "-", "·": "-",
  "…": "...", " ": " ", "\t": " ", "​": "",
};

const esGsm = (c) => GSM_BASICO.includes(c) || GSM_EXTENDIDO.includes(c);
const soloDigitos = (v) => String(v || "").replace(/\D/g, "");

export default {
  name: "ModalSendSms",
  props: {
    persona: { type: Object, required: true }, // titular o cotitular tal como lo muestra el perfil
    memberId: { type: String, required: true },
    contratoId: { type: [Number, String], required: true },
    rol: { type: String, default: "T" }, // T titular, C cotitular
    paises: { type: Array, default: () => [] }, // CatPaises, para la lada del país si no hay otra
    iso: { type: String, default: "es" },
  },
  data() {
    return {
      telefono: "",
      numeroOrigen: "",
      mensaje: "",
      enviando: false,
      maxSms: MAX_SMS,
      maxSeptetos: SEPTETOS_POR_PARTE * MAX_SMS,
    };
  },
  computed: {
    nombre() {
      return [this.persona.Nombre, this.persona.ApellidoPaterno].filter(Boolean).join(" ");
    },
    rolLabel() {
      return this.rol === "C" ? this.t("Co-Titular", "Co-Owner") : this.t("Titular", "Owner");
    },
    telefonoValido() {
      const digitos = soloDigitos(this.telefono);
      return /^\s*\+/.test(this.telefono) && digitos.length >= 8 && digitos.length <= 15;
    },
    esUsa() {
      return soloDigitos(this.telefono).startsWith("1");
    },
    mensajeFinal() {
      return this.mensaje.trim();
    },
    noPermitidos() {
      return [...new Set([...this.mensajeFinal].filter((c) => !esGsm(c)))];
    },
    textoCorregido() {
      return [...this.mensajeFinal].map((c) => (esGsm(c) ? c : REEMPLAZOS[c] !== undefined ? REEMPLAZOS[c] : "")).join("");
    },
    septetos() {
      return [...this.mensajeFinal].reduce((n, c) => n + (GSM_EXTENDIDO.includes(c) ? 2 : 1), 0);
    },
    sms() {
      if (!this.septetos) return 0;
      return this.septetos <= SEPTETOS_UN_SMS ? 1 : Math.ceil(this.septetos / SEPTETOS_POR_PARTE);
    },
    excedeMaximo() {
      return this.sms > MAX_SMS;
    },
    puedeEnviar() {
      return (
        this.telefonoValido &&
        this.mensajeFinal.length > 0 &&
        this.noPermitidos.length === 0 &&
        !this.excedeMaximo &&
        !this.enviando
      );
    },
  },
  created() {
    this.precargarTelefono();
  },
  methods: {
    t(es, en) {
      return this.iso === "es" ? es : en;
    },
    // Lada del celular, si no la del teléfono, si no la del país; número: celular o teléfono
    precargarTelefono() {
      const p = this.persona;
      const usaCelular = !!soloDigitos(p.Celular);
      const numero = soloDigitos(usaCelular ? p.Celular : p.Telefono);
      const ladaPais = this.paises.find((c) => c.Id === p.Pais)?.Lada;
      const lada = soloDigitos((usaCelular ? p.LadaCelular : p.LadaTelefono) || p.LadaCelular || p.LadaTelefono || ladaPais);

      if (!numero) {
        this.telefono = lada ? `+${lada}` : "+";
        this.numeroOrigen = this.t("El socio no tiene teléfono guardado", "No phone saved for this member");
        return;
      }
      // Si el número ya trae la lada al inicio no se repite
      this.telefono = lada && !numero.startsWith(lada) ? `+${lada}${numero}` : `+${numero}`;
      this.numeroOrigen =
        this.t("Tomado de ", "Taken from ") +
        (usaCelular ? this.t("celular", "cellphone") : this.t("teléfono", "phone")) +
        (lada ? "" : this.t(" (sin código de país: agrégalo)", " (no country code: add it)"));
    },
    visible(c) {
      if (c === " ") return "[espacio especial]";
      if (c === "\t") return "[tab]";
      return c;
    },
    corregir() {
      this.mensaje = this.textoCorregido;
    },
    confirmar() {
      this.$buefy.dialog.confirm({
        title: this.t("Confirmar envío", "Confirm sending"),
        message:
          this.t(`Se enviará a <b>${this.escapar(this.nombre)}</b> al <b>${this.telefono}</b>`, `It will be sent to <b>${this.escapar(this.nombre)}</b> at <b>${this.telefono}</b>`) +
          ` — <b>${this.sms} SMS</b>:<br><br><i>${this.escapar(this.mensajeFinal)}</i>`,
        confirmText: this.t(`Enviar ${this.sms} SMS`, `Send ${this.sms} SMS`),
        cancelText: this.t("Revisar", "Review"),
        type: "is-primary",
        onConfirm: () => this.enviar(),
      });
    },
    // El SMS ya salió aunque HubSpot falle: solo se informa qué pasó con la nota
    textoHubspot(hubspot) {
      if (!hubspot) return "";
      if (hubspot.success) return this.t(" · Nota registrada en HubSpot", " · Note saved in HubSpot");
      if (hubspot.skipped) return this.t(" · El socio no está vinculado a HubSpot", " · Member is not linked to HubSpot");
      return this.t(" · No se pudo registrar la nota en HubSpot", " · Could not save the note in HubSpot");
    },
    escapar(texto) {
      return String(texto).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    },
    async enviar() {
      try {
        this.enviando = true;
        const { data } = await axios.post(`${API_URL}/catcher/members/sms/send`, {
          ContratoId: this.contratoId,
          MemberId: this.memberId,
          Telefono: this.telefono,
          Mensaje: this.mensajeFinal,
          CreadoPor: localStorage.getItem("user"),
        });
        this.$buefy.toast.open({
          message: data.simulado
            ? this.t("SMS simulado (sin SMTP2GO configurado): quedó en Comentarios", "Simulated SMS (SMTP2GO not configured): saved in Comments")
            : this.t(`${data.sms} SMS enviado(s) a ${data.telefono}`, `${data.sms} SMS sent to ${data.telefono}`) + this.textoHubspot(data.hubspot),
          type: data.simulado || (data.hubspot && !data.hubspot.success && !data.hubspot.skipped) ? "is-warning" : "is-success",
          duration: 6000,
        });
        this.$bus.emit("refresh-comments");
        this.$emit("sent", data);
        this.$emit("close");
      } catch (error) {
        const msg = error?.response?.data?.error;
        this.$buefy.toast.open({
          message: msg || this.t("No se pudo enviar el SMS", "Could not send the SMS"),
          type: "is-danger",
          duration: 6000,
        });
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>

<style scoped>
.sms-contador {
  font-size: 0.85rem;
  color: #4a4a4a;
  background: #f5f5f5;
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
}
.sms-contador.is-varios {
  background: #fffaeb;
  color: #946c00;
}
.sms-contador.is-excedido {
  background: #feecf0;
  color: #cc0f35;
}
.sms-char {
  display: inline-block;
  min-width: 1.4rem;
  text-align: center;
  margin: 0 0.15rem;
  padding: 0 0.3rem;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #f14668;
  font-weight: bold;
}
</style>
